/** Shared helpers for surfacing axios/FastAPI error responses in the UI. */

import {
  extractLimitContext,
  formatMonthlyBudgetLimitMessage,
} from "./lifecycleErrors";

type ApiErrorResponse = {
  response?: {
    status?: number;
    data?: {
      detail?: unknown;
      message?: string;
      limit_context?: {
        used?: number;
        budget?: number;
        additional?: number;
      };
    };
  };
};

function asApiError(error: unknown): ApiErrorResponse | null {
  if (typeof error === "object" && error !== null && "response" in error) {
    return error as ApiErrorResponse;
  }
  return null;
}

function extractDetailFromApiError(error: unknown): string | null {
  const apiError = asApiError(error);
  const detail = apiError?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length > 0 && typeof detail[0]?.msg === "string") {
    return detail[0].msg;
  }
  if (apiError?.response?.data?.message) return apiError.response.data.message;
  return null;
}

/** Extract a human-readable message from an axios error, falling back when absent. */
export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  return extractDetailFromApiError(error) ?? fallback;
};

/** HTTP status code for an axios error, or null when unavailable. */
export const getApiErrorStatus = (error: unknown): number | null => {
  return asApiError(error)?.response?.status ?? null;
};

const ACCOUNT_DUPLICATE_MESSAGE = "An account with this domain already exists.";
const ASSET_LINK_DUPLICATE_MESSAGE = "This link is already in your asset library.";
const ASSET_DUPLICATE_FALLBACK_MESSAGE = "An asset with the same details already exists.";

/** User-facing message for POST /accounts/ failures (409 = duplicate domain for client). */
export const getAccountCreateErrorMessage = (error: unknown): string => {
  const status = getApiErrorStatus(error);
  if (status === 409) {
    return ACCOUNT_DUPLICATE_MESSAGE;
  }
  if (status === 402) {
    const detail = extractDetailFromApiError(error);
    const limitContext = extractLimitContext(error);
    if (limitContext?.budget != null) {
      return formatMonthlyBudgetLimitMessage(detail, {
        action: "create",
        limitContext,
      });
    }
    if (detail?.toLowerCase().includes("budget")) {
      return detail;
    }
    return formatMonthlyBudgetLimitMessage(detail, { action: "create" });
  }
  return getApiErrorMessage(error, "Failed to create account. Please try again.");
};

export type AssetUploadErrorContext = {
  isLink?: boolean;
};

/** User-facing message for POST /assets/ failures (409 = duplicate link or unique violation). */
export const getAssetUploadErrorMessage = (
  error: unknown,
  context?: AssetUploadErrorContext
): string => {
  if (getApiErrorStatus(error) === 409) {
    return context?.isLink ? ASSET_LINK_DUPLICATE_MESSAGE : ASSET_DUPLICATE_FALLBACK_MESSAGE;
  }
  return getApiErrorMessage(error, "Failed to upload asset. Please try again.");
};
